import {
  AUTOMATION_CATEGORY_NAME_MAX_LENGTH,
  type AutomationCategoryResponse,
} from '@reach/shared-types/automation-category';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useAutomationCategories } from '../queries';
import { useCategoryCreator } from '../useCategoryCreator';
import styles from './CategoryCreateForm.module.css';

/** Creates a partner category from the templates list Categories menu. */
interface CategoryCreateFormProps {
  isFirstCategory: boolean;
  onCreated: (category: AutomationCategoryResponse) => void;
}

export function CategoryCreateForm({ isFirstCategory, onCreated }: CategoryCreateFormProps) {
  const [name, setName] = useState('');
  const { create, isPending } = useCategoryCreator();
  const { data: categories = [] } = useAutomationCategories();

  const trimmedName = name.trim();
  const normalizedName = trimmedName.toLowerCase();
  const nameIsTaken = categories.some(category => category.name.toLowerCase() === normalizedName);
  const canCreate = trimmedName.length > 0 && !nameIsTaken && !isPending;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canCreate) return;
    create(trimmedName, category => {
      setName('');
      onCreated(category);
    });
  };

  return (
    <form className={styles.createForm} data-first={isFirstCategory} onSubmit={handleSubmit}>
      {isFirstCategory && <p>No categories yet. Create one to organize your automations.</p>}
      <div>
        <input
          type="text"
          value={name}
          maxLength={AUTOMATION_CATEGORY_NAME_MAX_LENGTH}
          placeholder="e.g. Seasonal"
          aria-label="New category name"
          onChange={event => setName(event.target.value)}
        />
        <button type="submit" disabled={!canCreate}>
          <Plus aria-hidden />
          Create
        </button>
      </div>
      {isFirstCategory && (
        <small>Assign it to automations from their settings or the “+” on each row.</small>
      )}
    </form>
  );
}
